/**
 * Which Publish sheet is open on web: at most one, drawn by
 * `PublishSheetHost` in the protected shell, because its openers (the canvas
 * panel's Share menu, the site-publish card, the Sites list and the settings
 * page) live in different trees. Native opens the `/site-publish` form sheet
 * route instead (`open-publish-sheet.ts`).
 */
import { useSyncExternalStore } from 'react';
import type { PublishSheetIntent } from './publish-sheet-model';

export type PublishSheetRequest = {
  artifactId: string;
  intent: PublishSheetIntent;
  /** The `publishArtifact` card's suggestion, when the artifact has no address yet. */
  suggestedName?: string | null;
  /** The artifact's title, to seed the address and label the dialog. */
  title?: string | null;
  /** Changes on every open, so opening the same artifact again remounts the sheet. */
  nonce: number;
};

let request: PublishSheetRequest | null = null;
let counter = 0;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

export function showPublishSheet(next: Omit<PublishSheetRequest, 'nonce'>) {
  counter += 1;
  request = { ...next, nonce: counter };
  notify();
}

export function hidePublishSheet() {
  if (!request) return;
  request = null;
  notify();
}

export function readPublishSheet(): PublishSheetRequest | null {
  return request;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function usePublishSheetRequest(): PublishSheetRequest | null {
  return useSyncExternalStore(subscribe, readPublishSheet, () => null);
}
